import React, { useState, useEffect } from 'react'
import { throttle, debounce } from 'lodash'
import MUIDataTable from 'mui-datatables'
import { useDispatch, useSelector } from 'react-redux'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import { DatePicker } from '@material-ui/pickers'
import { ThemeProvider } from '@material-ui/core/styles'
import { getMuiThemeWithoutPadding } from '../../utils/TableStyle'
import { getUsersPackages, getAllPackages, extendPackageExpiryDate } from '../../app/slices/packageSlice'
import muiDefaultOptions from '../../constants/muiTableOptions'
import { failed } from '../../app/slices/snackSlice'

const PAYMENT_STATUS = {
  100: 'در انتظار پرداخت',
  101: 'پرداخت شده',
  102: 'در حال پرداخت',
  103: 'تکمیل شده',
  104: 'منقضی شده',
  105: 'تایید نتیجه',
}

const NAME = 'user.firstName'
const NATIONAL_CODE = 'user.userName'
const PACKAGE_NAME = 'package.name'
const STATUS = 'status'

export const ExamsList = () => {
  const dispatch = useDispatch()
  const role = useSelector((state) => state.user?.role)
  const count = useSelector((state) => state.package.usersPackages.totalHits)
  const packages = useSelector((state) => state.package.usersPackages.list)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(muiDefaultOptions.rowsPerPage)

  const [paymentId, setPaymentId] = useState(null)
  const [paymentStatus, setPaymentStatus] = useState(null)
  const [selectedPackage, setSelectedPackage] = useState(false)
  const [expiredDate, handleDateChange] = useState()

  const parseUsersString = () => {
    return paymentId?.split(',')?.map((item) => parseInt(item, 10))
  }

  useEffect(() => {
    dispatch(getUsersPackages({
      first: rowsPerPage,
      after: page * rowsPerPage,
      UserIds: parseUsersString() || undefined,
      Statuses: paymentStatus?.map((status) => parseInt(status, 10)) || undefined,
    }))
  }, [rowsPerPage, page, paymentId, paymentStatus])

  React.useEffect(() => {
    if ([2, 3].includes(role?.roleId)) {
      dispatch(getAllPackages())
    }
  }, [])

  const COLUMNS = [
    {
      name: NAME,
      label: 'کاربر',
      options: {
        sort: false,
        filter: true,
        filterType: 'textField',
        customBodyRenderLite: (index) => {
          const { firstName = '', lastName = '' } = packages[index].user
          if (firstName && lastName) return `${lastName} ${firstName}`
          return '--'
        },
      },
    },
    {
      name: NATIONAL_CODE,
      label: 'کد ملی',
      options: {
        sort: false,
        filter: false,
      },
    },
    {
      name: PACKAGE_NAME,
      label: 'بسته خریداری شده',
      options: {
        sort: false,
        filter: false,
      },
    },
    {
      name: STATUS,
      label: 'وضعیت',
      options: {
        sort: false,
        filter: true,
        filterType: 'multiselect',
        customBodyRender: (status) => PAYMENT_STATUS[status],
        filterOptions: {
          names: Object.keys(PAYMENT_STATUS)?.map?.((key) => PAYMENT_STATUS[key]),
        },
      },
    },
    {
      name: '',
      label: '',
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (index) => {
          const data = packages[index]
          if (data.status === 104) {
            return (
              <Grid container justify="flex-end">
                <Button
                  variant="contained"
                  onClick={() => setSelectedPackage(data)}
                >
                  تمدید بسته
                </Button>
              </Grid>
            )
          }
          return null
        },
      },
    },
  ]

  const updatePaymentId = debounce((value) => {
    setPaymentId(value)
  }, 500, { maxWait: 1000 })

  const updateFilters = throttle((state) => {
    const idColumnIdx = state.columns.findIndex(
      (col) => col.name === NAME,
    )
    const statusColumnIdx = state.columns.findIndex(
      (col) => col.name === STATUS,
    )

    if (state.filterList[idColumnIdx].length > 0) {
      updatePaymentId(state.filterList[idColumnIdx][0])
    } else {
      updatePaymentId(null)
    }

    if (state.filterList[statusColumnIdx].length) {
      const filterArray = state.filterList[statusColumnIdx]
      const keys = Object.keys(PAYMENT_STATUS)
      const selectedKeys = keys.filter((key) => {
        return filterArray.findIndex((status) => status === PAYMENT_STATUS[key]) >= 0
      })
      setPaymentStatus(selectedKeys)
    } else {
      setPaymentStatus(null)
    }
  })

  const resetFilters = () => {
    setPaymentStatus(null)
    setPaymentId(null)
  }

  const handleClose = () => {
    handleDateChange(null)
    setSelectedPackage(null)
  }

  const handleExtending = () => {
    if (!expiredDate) {
      dispatch(failed('لطفا تاریخ مورد نظر را انتخاب کنید'))
      return
    }
    dispatch(extendPackageExpiryDate({
      id: selectedPackage.id,
      until: expiredDate.toISOString(),
    }, () => {
      dispatch(getUsersPackages({
        first: rowsPerPage,
        after: page * rowsPerPage,
        UserIds: parseInt(paymentId, 10),
        Statuses: paymentStatus?.map((status) => parseInt(status, 10)) || null,
      }))
      handleClose()
    }))
  }

  return (
    <>
      <ThemeProvider theme={getMuiThemeWithoutPadding}>
        <MUIDataTable
          data={packages}
          columns={COLUMNS}
          classes={{}}
          options={{
            ...muiDefaultOptions,

            enableNestedDataAccess: '.',
            rowsPerPage,
            page,
            count,

            serverSide: true,
            print: false,
            download: false,
            search: false,

            rowHover: true,
            selectableRows: 'none',
            searchable: false,

            onTableChange: (action, state) => {
              switch (action) {
                case 'changePage': {
                  setPage(state.page)
                  break
                }
                case 'changeRowsPerPage': {
                  setRowsPerPage(state.rowsPerPage)
                  break
                }
                case 'filterChange':
                  updateFilters(state)
                  break
                case 'resetFilters': {
                  resetFilters()
                  break
                }
                default: {
                  return null
                }
              }
              return null
            },
          }}
        />
      </ThemeProvider>
      <Dialog
        open={selectedPackage}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">تمدید بسته</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            لطفا تاریخ انقضای جدید را وارد کنید
          </DialogContentText>
          <DatePicker
            required
            inputVariant="outlined"
            size="small"
            label="تاریخ انقضا"
            fullWidth
            clearable
            okLabel="تأیید"
            cancelLabel="لغو"
            clearLabel="پاک کردن"
            labelFunc={(date) => (date ? date.format('jYYYY/jMM/jDD') : '')}
            value={expiredDate}
            onChange={handleDateChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            انصراف
          </Button>
          <Button onClick={handleExtending} color="primary" autoFocus>
            تایید
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ExamsList
